import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Flame, Snowflake, TrendingUp, Sparkles, Brain, Star, Calculator, Hash, Copy, Check, ChevronDown, ChevronUp, Zap } from "lucide-react";
import { soundManager } from "@/lib/soundManager";
import { toast } from "sonner";

interface PredictionCardProps {
  prediction: {
    number: string;
    confidence: number;
    reasoning: string[];
    predictionType?: "date_based" | "ai_predicted" | "cosmic" | "mathematical" | "numerological";
    statistics: {
      frequency: number;
      lastSeen: string | null;
      daysSinceLastSeen: number | null;
      hotColdStatus: "hot" | "cold" | "neutral";
      positions: { first: number; second: number; third: number };
    };
    mathematicalAnalysis?: {
      digitSum: number;
      digitalRoot: number;
      isPrime: boolean;
      isEven: boolean;
      hasRepeatingDigits: boolean;
      uniqueDigits: number;
    };
    dateAnalysis?: any;
    // Phase 2 fields
    engineScores?: {
      mathematics: number;
      numerology: number;
      astrology: number;
      statistics: number;
    };
    cosmicContext?: string;
    numerologyContext?: string;
    methodCount?: number;
    methods?: string[];
    breakdown?: {
      math: string[];
      numerology: string[];
      astrology: string[];
      statistics: string[];
    };
  };
  rank: number;
}

const PREDICTION_TYPE_CONFIG = {
  date_based: { label: "📅 Date-Based", color: "bg-purple-500/20 text-purple-300 border-purple-500/50" },
  ai_predicted: { label: "🤖 AI Ensemble", color: "bg-blue-500/20 text-blue-300 border-blue-500/50" },
  cosmic: { label: "🌟 Cosmic", color: "bg-amber-500/20 text-amber-300 border-amber-500/50" },
  mathematical: { label: "📐 Mathematical", color: "bg-green-500/20 text-green-300 border-green-500/50" },
  numerological: { label: "🔢 Numerology", color: "bg-pink-500/20 text-pink-300 border-pink-500/50" },
};

export function PredictionCard({ prediction, rank }: PredictionCardProps) {
  const [isFlipped, setIsFlipped] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showFrameworks, setShowFrameworks] = useState(false);

  const handleFlip = () => {
    soundManager.playClick();
    setIsFlipped(!isFlipped);
  };

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(number);
    setCopied(true);
    toast.success(`Copied ${number} to clipboard!`);
    setTimeout(() => setCopied(false), 2000);
  };

  const { number, confidence, reasoning, statistics, mathematicalAnalysis, predictionType, engineScores, methodCount, methods, breakdown } = prediction;

  const getStatusColor = () => {
    if (statistics.hotColdStatus === "hot") return "text-red-400";
    if (statistics.hotColdStatus === "cold") return "text-blue-400";
    return "text-gray-400";
  };

  const getStatusIcon = () => {
    if (statistics.hotColdStatus === "hot") return <Flame className="w-4 h-4" />;
    if (statistics.hotColdStatus === "cold") return <Snowflake className="w-4 h-4" />;
    return <TrendingUp className="w-4 h-4" />;
  };

  const typeConfig = predictionType ? PREDICTION_TYPE_CONFIG[predictionType] : PREDICTION_TYPE_CONFIG.ai_predicted;

  // Determine card border color based on confidence
  const getBorderColor = () => {
    if (confidence >= 0.80) return "border-yellow-500/70";
    if (confidence >= 0.65) return "border-amber-500/50";
    if (confidence >= 0.50) return "border-blue-500/50";
    return "border-slate-700";
  };

  // Get glow effect for high confidence
  const getGlow = () => {
    if (confidence >= 0.80) return "shadow-lg shadow-yellow-500/20";
    if (confidence >= 0.65) return "shadow-md shadow-amber-500/15";
    return "";
  };

  return (
    <div className="perspective-1000">
      <div
        className={`relative w-full transition-transform duration-500 transform-style-3d cursor-pointer`}
        style={{ minHeight: "280px" }}
        onClick={handleFlip}
      >
        {/* Front of Card */}
        {!isFlipped && (
          <Card
            className={`w-full p-5 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-2 ${getBorderColor()} ${getGlow()}`}
          >
            {/* Top Row: Rank + Type + Status */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs font-bold bg-slate-700 text-slate-200">
                  #{rank}
                </Badge>
                {predictionType && (
                  <Badge variant="outline" className={`text-xs ${typeConfig.color}`}>
                    {typeConfig.label}
                  </Badge>
                )}
              </div>
              <Badge variant="outline" className={`${getStatusColor()} border-current flex items-center gap-1 text-xs`}>
                {getStatusIcon()}
                {statistics.hotColdStatus.toUpperCase()}
              </Badge>
            </div>

            {/* Number Display */}
            <div className="flex flex-col items-center py-3">
              <div className={`text-6xl font-bold tracking-wider text-transparent bg-clip-text ${
                confidence >= 0.80 
                  ? "bg-gradient-to-r from-yellow-300 via-yellow-400 to-yellow-500"
                  : confidence >= 0.65
                  ? "bg-gradient-to-r from-amber-300 via-amber-400 to-orange-400"
                  : "bg-gradient-to-r from-blue-300 via-blue-400 to-blue-500"
              } mb-3`}>
                {number}
              </div>

              {/* Confidence Bar */}
              <div className="w-full max-w-xs space-y-1.5">
                <div className="flex justify-between text-xs text-gray-400">
                  <span>Master Confidence</span>
                  <span className={`font-bold ${confidence >= 0.80 ? "text-yellow-400" : confidence >= 0.65 ? "text-amber-400" : "text-blue-400"}`}>
                    {(confidence * 100).toFixed(1)}%
                  </span>
                </div>
                <Progress
                  value={confidence * 100}
                  className={`h-2.5 bg-slate-700 ${
                    confidence >= 0.80 
                      ? "[&>div]:bg-gradient-to-r [&>div]:from-yellow-400 [&>div]:to-yellow-600"
                      : confidence >= 0.65
                      ? "[&>div]:bg-gradient-to-r [&>div]:from-amber-400 [&>div]:to-orange-500"
                      : "[&>div]:bg-gradient-to-r [&>div]:from-blue-400 [&>div]:to-blue-600"
                  }`}
                />
              </div>
            </div>

            {/* Engine Score Mini-bars */}
            {engineScores && (
              <div className="grid grid-cols-4 gap-1.5 mt-2 mb-3">
                {[
                  { label: "Math", score: engineScores.mathematics, icon: "📐", color: "bg-green-500" },
                  { label: "Num", score: engineScores.numerology, icon: "🔢", color: "bg-pink-500" },
                  { label: "Astro", score: engineScores.astrology, icon: "🌟", color: "bg-amber-500" },
                  { label: "Stats", score: engineScores.statistics, icon: "📊", color: "bg-blue-500" },
                ].map(({ label, score, icon, color }) => (
                  <div key={label} className="text-center">
                    <div className="text-xs text-gray-500 mb-1">{icon} {label}</div>
                    <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${color} rounded-full transition-all`}
                        style={{ width: `${(score || 0) * 100}%` }}
                      />
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">{score > 0 ? `${(score * 100).toFixed(0)}%` : "—"}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Quick Stats Row */}
            <div className="grid grid-cols-3 gap-2 text-center text-xs border-t border-slate-700 pt-3">
              <div>
                <div className="text-gray-500">Freq</div>
                <div className="text-white font-semibold">{statistics.frequency}×</div>
              </div>
              <div>
                <div className="text-gray-500">Engines</div>
                <div className="text-white font-semibold">{methodCount || 1}/4</div>
              </div>
              <div>
                <div className="text-gray-500">Last Seen</div>
                <div className="text-white font-semibold">
                  {statistics.daysSinceLastSeen !== null ? `${statistics.daysSinceLastSeen}d` : "Never"}
                </div>
              </div>
            </div>

            {/* Copy button + Tap hint */}
            <div className="mt-3 flex items-center justify-between gap-2">
              <div className="text-xs text-gray-600 flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Tap for full analysis
              </div>
              <button
                onClick={handleCopy}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  copied
                    ? "bg-green-500/20 text-green-400 border border-green-500/40"
                    : "bg-blue-500/20 text-blue-300 border border-blue-500/40 hover:bg-blue-500/30"
                }`}
              >
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
          </Card>
        )}

        {/* Back of Card — Full Analysis */}
        {isFlipped && (
          <Card className="w-full p-5 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 border-2 border-yellow-500/30 overflow-y-auto max-h-[500px]">
            {/* Header */}
            <div className="flex items-center justify-between mb-4 sticky top-0 bg-slate-900/95 py-1 -mx-1 px-1 rounded">
              <div className="flex items-center gap-2">
                <div className="text-2xl font-bold text-yellow-400">{number}</div>
                <Badge variant="outline" className="text-yellow-500 border-yellow-500/50 text-xs">
                  {(confidence * 100).toFixed(1)}% confidence
                </Badge>
              </div>
              <Badge variant="outline" className="text-xs text-slate-400">
                Analysis
              </Badge>
            </div>

            {/* Engine Scores Detailed */}
            {engineScores && (
              <div className="mb-4 p-3 bg-slate-800/80 rounded-lg border border-slate-700">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-1">
                  <Brain className="w-3 h-3" /> Multi-Engine Breakdown
                </h3>
                <div className="space-y-2">
                  {[
                    { label: "📐 Mathematics", score: engineScores.mathematics, color: "from-green-500 to-emerald-400" },
                    { label: "🔢 Numerology", score: engineScores.numerology, color: "from-pink-500 to-rose-400" },
                    { label: "🌟 Astrology", score: engineScores.astrology, color: "from-amber-500 to-yellow-400" },
                    { label: "📊 Statistics", score: engineScores.statistics, color: "from-blue-500 to-cyan-400" },
                  ].map(({ label, score, color }) => (
                    <div key={label}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-300">{label}</span>
                        <span className={score > 0 ? "text-yellow-400 font-semibold" : "text-gray-600"}>
                          {score > 0 ? `${(score * 100).toFixed(0)}%` : "N/A"}
                        </span>
                      </div>
                      <Progress value={(score || 0) * 100} className={`h-1.5 bg-slate-700 [&>div]:bg-gradient-to-r [&>div]:${color}`} />
                    </div>
                  ))}
                </div>

                {/* Framework-by-Framework Breakdown — expandable */}
                {breakdown && (
                  <div className="mt-3">
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowFrameworks(!showFrameworks); }}
                      className="w-full flex items-center justify-between text-xs text-blue-400 hover:text-blue-300 transition-colors py-1.5 border-t border-slate-700 mt-2"
                    >
                      <span className="flex items-center gap-1 font-semibold">
                        <Zap className="w-3 h-3" />
                        Framework Votes ({(breakdown.math.length + breakdown.numerology.length + breakdown.astrology.length + breakdown.statistics.length)} frameworks)
                      </span>
                      {showFrameworks ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>

                    {showFrameworks && (
                      <div className="mt-2 space-y-3">
                        {[
                          { key: 'math', label: '📐 Mathematics', items: breakdown.math, color: 'text-green-400', bg: 'bg-green-500/10 border-green-500/20' },
                          { key: 'numerology', label: '🔢 Numerology', items: breakdown.numerology, color: 'text-pink-400', bg: 'bg-pink-500/10 border-pink-500/20' },
                          { key: 'astrology', label: '🌟 Astrology', items: breakdown.astrology, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
                          { key: 'statistics', label: '📊 Statistics', items: breakdown.statistics, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20' },
                        ].filter(({ items }) => items.length > 0).map(({ key, label, items, color, bg }) => (
                          <div key={key} className={`rounded-lg border p-2 ${bg}`}>
                            <div className={`text-xs font-semibold ${color} mb-1.5`}>{label}</div>
                            <div className="space-y-1">
                              {items.map((item, idx) => {
                                // Parse "method_name: reasoning..." format
                                const colonIdx = item.indexOf(':');
                                const methodName = colonIdx > 0 ? item.slice(0, colonIdx).trim() : item;
                                const methodDesc = colonIdx > 0 ? item.slice(colonIdx + 1).trim() : '';
                                // Format method name nicely
                                const niceMethod = methodName
                                  .replace(/_/g, ' ')
                                  .replace(/\b\w/g, c => c.toUpperCase());
                                return (
                                  <div key={idx} className="text-xs">
                                    <span className={`font-semibold ${color}`}>{niceMethod}</span>
                                    {methodDesc && (
                                      <span className="text-gray-500 ml-1">
                                        — {methodDesc.slice(0, 80)}{methodDesc.length > 80 ? '...' : ''}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Reasoning */}
            <div className="space-y-2 mb-4">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-1">
                <Star className="w-3 h-3" /> Why This Number?
              </h3>
              {reasoning.map((reason, index) => (
                <div key={index} className="flex items-start gap-2 text-xs text-gray-300 leading-relaxed">
                  <span className="text-yellow-500 mt-0.5 shrink-0">•</span>
                  <span>{reason}</span>
                </div>
              ))}
            </div>

            {/* Mathematical Analysis */}
            {mathematicalAnalysis && (
              <div className="mb-4 p-3 bg-slate-800/80 rounded-lg border border-slate-700">
                <h4 className="text-xs font-semibold text-gray-400 mb-2 flex items-center gap-1">
                  <Calculator className="w-3 h-3" /> Mathematical Properties
                </h4>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="text-center">
                    <div className="text-gray-500">Digit Sum</div>
                    <div className="text-white font-semibold">{mathematicalAnalysis.digitSum}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-gray-500">Digital Root</div>
                    <div className="text-white font-semibold">{mathematicalAnalysis.digitalRoot}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-gray-500">Type</div>
                    <div className={`font-semibold ${mathematicalAnalysis.isPrime ? "text-green-400" : "text-gray-300"}`}>
                      {mathematicalAnalysis.isPrime ? "Prime" : mathematicalAnalysis.isEven ? "Even" : "Odd"}
                    </div>
                  </div>
                </div>
                <div className="mt-2 flex gap-2 flex-wrap">
                  {mathematicalAnalysis.isPrime && (
                    <Badge variant="outline" className="text-xs bg-green-500/10 text-green-400 border-green-500/30">🔷 Prime</Badge>
                  )}
                  {mathematicalAnalysis.hasRepeatingDigits && (
                    <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-400 border-purple-500/30">🔁 Repeating</Badge>
                  )}
                  <Badge variant="outline" className="text-xs bg-slate-700 text-slate-300">
                    <Hash className="w-2 h-2 mr-1" />{mathematicalAnalysis.uniqueDigits} unique digits
                  </Badge>
                </div>
              </div>
            )}

            {/* Position Breakdown */}
            {statistics.frequency > 0 && (
              <div className="p-3 bg-slate-800/80 rounded-lg border border-slate-700">
                <h4 className="text-xs font-semibold text-gray-400 mb-2">Prize Position History</h4>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="text-center">
                    <div className="text-yellow-400 font-bold text-lg">{statistics.positions.first}</div>
                    <div className="text-gray-400">1st Prize</div>
                  </div>
                  <div className="text-center">
                    <div className="text-gray-300 font-bold text-lg">{statistics.positions.second}</div>
                    <div className="text-gray-400">2nd Prize</div>
                  </div>
                  <div className="text-center">
                    <div className="text-amber-700 font-bold text-lg">{statistics.positions.third}</div>
                    <div className="text-gray-400">3rd Prize</div>
                  </div>
                </div>
              </div>
            )}

            {/* Tap to flip back */}
            <div className="mt-4 text-xs text-gray-600 text-center flex items-center justify-center gap-1">
              <Sparkles className="w-3 h-3" />
              Tap to flip back
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
